<script>
import SpellCheckedText from "@/components/SpellCheckedText.vue";
import PhotoBox from "@/components/PhotoBox.vue";

export default {
    name: "TOCTemp6",
    components: {
        SpellCheckedText,
        PhotoBox
    },
    props: {
        toc: { type: Object, required: true },
        pageGap: { type: Boolean, default: true },
    },
    data() {
        return {
            pages: [],
            footerImage: ""
        };
    },
    watch: {
        toc: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.splitContentIntoPages();
                });
            }
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.generateGradients();
            this.splitContentIntoPages();
        });
    },
    methods: {
        getAlphabet(index) {
            let result = "";
            index += 1;
            while (index > 0) {
                let rem = (index - 1) % 26;
                result = String.fromCharCode(65 + rem) + result;
                index = Math.floor((index - 1) / 26);
            }
            return result.toUpperCase();
        },
        splitContentIntoPages() {
            if (!this.toc || !this.toc.pages) return;

            const itemsPerPage = parseInt(this.toc.itemsPerPage);
            const half = Math.ceil(itemsPerPage / 2);
            const projects = this.toc.projects || [];
            let allTocItems = [...this.toc.pages].map((item, index) => ({
                ...item,
                alphabet: this.getAlphabet(index)
            }));

            this.pages = [];
            let pagesProjectsIndex = 0;

            while (allTocItems.length > 0) {
                let leftItems = allTocItems.splice(0, half);
                let rightItems = allTocItems.splice(0, itemsPerPage - leftItems.length);

                this.pages.push({
                    leftItems: leftItems,
                    rightItems: rightItems,
                    project: projects[pagesProjectsIndex] || null
                });
                pagesProjectsIndex++;

                if (this.pages.length > 50) break;
            }

            if (this.pages.length === 0) {
                this.pages.push({ leftItems: [], rightItems: [], project: projects[0] || null });
            }
        },
        generateGradients() {
            const fCanvas = document.createElement('canvas');
            fCanvas.width = 1130;
            fCanvas.height = 180;
            const fCtx = fCanvas.getContext('2d');
            const fGrad = fCtx.createLinearGradient(0, 180, 0, 0);
            fGrad.addColorStop(0, 'rgba(0, 0, 0, 0.7)');
            fGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
            fCtx.fillStyle = fGrad;
            fCtx.fillRect(0, 0, 1130, 180);
            this.footerImage = fCanvas.toDataURL();
        }
    }
};
</script>

<template>
    <div class="main-container" :style="{
        gap: pageGap ? '20px' : '0px',
        '--header-color': (toc.style.colorScheme && toc.style.colorScheme.header) || '#1b5e20',
        '--body-color': (toc.style.colorScheme && toc.style.colorScheme.body) || '#ffffff',
        '--footer-color': (toc.style.colorScheme && toc.style.colorScheme.footer) || '#ffffff',
        '--heading-color': toc.style.heading.color || '#ffffff',
        '--heading-size': toc.style.heading.size + 'px',
        '--heading-weight': toc.style.heading.bold ? 'bold' : 'normal',
        '--heading-style': toc.style.heading.italic ? 'italic' : 'normal',
        '--heading-decoration': toc.style.heading.underline ? 'underline' : 'none',
        '--heading-font': toc.style.heading.font,

        '--label-color': toc.style.label.color || '#ffffff',
        '--label-size': toc.style.label.size + 'px',
        '--label-weight': toc.style.label.bold ? 'bold' : 'normal',
        '--label-style': toc.style.label.italic ? 'italic' : 'normal',
        '--label-decoration': toc.style.label.underline ? 'underline' : 'none',
        '--label-font': toc.style.label.font,

        '--text-color': toc.style.text.color || '#ffffff',
        '--text-size': toc.style.text.size + 'px',
        '--text-weight': toc.style.text.bold ? 'bold' : 'normal',
        '--text-style': toc.style.text.italic ? 'italic' : 'normal',
        '--text-decoration': toc.style.text.underline ? 'underline' : 'none',
        '--text-font': toc.style.text.font,
    }">
        <div class="sub-container" v-for="(page, pageIndex) in pages" :key="pageIndex">
            <div class="content">
                <!-- Top content holds TOC -->
                <div class="top-content">
                    <div :class="pageIndex > 0 && 'my-auto'">
                        <div v-if="pageIndex === 0" class="toc-header">
                            <h1 class="title">
                                <SpellCheckedText :value="toc.title || ''" @input="toc.title = $event" />
                            </h1>
                        </div>

                        <div class="toc-grid">
                            <div class="toc-column">
                                <div v-for="(item, idx) in page.leftItems" :key="'left-' + idx" class="toc-item">
                                    <span class="alphabet">{{ item.alphabet }}.</span>
                                    <span class="heading">
                                        <SpellCheckedText :value="item.heading || ''" @input="item.heading = $event" />
                                    </span>
                                    <div class="dots"></div>
                                    <span class="page-num">{{ String(item.page || '').padStart(2, '0') }}</span>
                                </div>
                            </div>

                            <div class="toc-column">
                                <div v-for="(item, idx) in page.rightItems" :key="'right-' + idx" class="toc-item">
                                    <span class="alphabet">{{ item.alphabet }}.</span>
                                    <span class="heading">
                                        <SpellCheckedText :value="item.heading || ''" @input="item.heading = $event" />
                                    </span>
                                    <div class="dots"></div>
                                    <span class="page-num">{{ String(item.page || '').padStart(2, '0') }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Bottom content holds Footer -->
                <div class="bottom-content">
                    <div class="bottom-section photobox">
                        <PhotoBox v-if="page.project" :project="page.project" :bgColor="toc.style.colorScheme && toc.style.colorScheme.body"
                            objectFit="cover" />
                        <div v-else class="bg-placeholder" />

                        <div v-if="page.project && footerImage" class="bottom-gradient">
                            <img :src="footerImage" class="w-full h-full" />
                        </div>

                        <div class="footer-section">
                            <div class="toc-footer">
                                <div class="footer-left">
                                    <div class="project-details">
                                        <div v-if="page.project" class="footer-line1">
                                            <p class="proj-name">
                                                <SpellCheckedText :value="page.project.projectName || ''"
                                                    @input="page.project.projectName = $event" />
                                            </p>
                                            <p class="proj-loc">
                                                <SpellCheckedText :value="page.project.city || ''"
                                                    @input="page.project.city = $event" />{{ page.project.city ? ', ' :
                                                        ''
                                                    }}
                                                <SpellCheckedText :value="page.project.stateProvince || ''"
                                                    @input="page.project.stateProvince = $event" />
                                            </p>
                                        </div>

                                        <p class="footer-line2">
                                            <SpellCheckedText :value="toc.opprName || ''"
                                                @input="toc.opprName = $event" />
                                        </p>
                                    </div>
                                </div>
                                <div class="footer-right">
                                    <p class="footer-line2">
                                        <SpellCheckedText :value="toc.submittedBy || ''"
                                            @input="toc.submittedBy = $event" />
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped lang="scss">
.main-container {
    display: flex;
    flex-direction: column;
    width: 296.83mm;
    min-height: 211mm;
    position: relative;
    box-sizing: border-box;
    background: white;

    &:hover {
        .photobox :deep(.photo-overlay) {
            opacity: 1;
        }

        .photobox :deep(img) {
            opacity: 0.9;
        }
    }

    .sub-container {
        position: relative;
        display: flex;
        flex-direction: column;
        width: 296.83mm;
        height: 211mm;
        min-height: 211mm;
        overflow: hidden;

        .content {
            height: 100%;
            display: flex;
            flex-direction: column;

            .top-content {
                display: flex;
                flex-direction: column;
                height: 50%;
                padding: 30px 80px;
                background: var(--header-color);

                .toc-header {
                    text-align: center;
                    margin-bottom: 20px;

                    .title {
                        font-size: var(--heading-size);
                        color: var(--heading-color);
                        font-family: var(--heading-font);
                        font-weight: var(--heading-weight);
                        font-style: var(--heading-style);
                        text-decoration: var(--heading-decoration);
                        margin: 0;
                    }
                }

                .toc-grid {
                    display: grid;
                    grid-template-columns: 1fr 1fr;
                    gap: 80px;

                    .toc-column {
                        display: flex;
                        flex-direction: column;
                        gap: 10px;

                        .toc-item {
                            display: flex;
                            align-items: flex-start;

                            .alphabet {
                                font-size: var(--label-size);
                                color: var(--label-color);
                                font-family: var(--label-font);
                                font-weight: var(--label-weight);
                                font-style: var(--label-style);
                                text-decoration: var(--label-decoration);
                                min-width: 25px;
                            }

                            .heading {
                                font-size: var(--text-size);
                                color: var(--text-color);
                                font-family: var(--text-font);
                                font-weight: var(--text-weight);
                                font-style: var(--text-style);
                                text-decoration: var(--text-decoration);
                                line-height: 1.2;
                                min-width: 0;
                            }

                            .dots {
                                flex: 1;
                            }

                            .page-num {
                                font-size: var(--label-size);
                                color: var(--label-color);
                                font-family: var(--label-font);
                                font-weight: var(--label-weight);
                                font-style: var(--label-style);
                                text-decoration: var(--label-decoration);
                                min-width: 25px;
                                text-align: right;
                                margin-left: 25px;
                            }
                        }
                    }
                }
            }

            .bottom-content {
                position: relative;
                display: flex;
                flex-direction: column;
                justify-content: flex-end;
                height: 50%;
                background: var(--body-color);

                .bottom-section {
                    width: 100%;
                    height: 100%;
                    z-index: 1;

                    .bg-placeholder {
                        width: 100%;
                        height: 100%;
                    }

                    .bottom-gradient {
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        height: 40%;
                        width: 100%;
                        z-index: 2;
                    }

                    .footer-section {
                        position: absolute;
                        bottom: 0;
                        left: 0;
                        right: 0;
                        z-index: 50;
                        padding: 0px 40px 20px 40px;

                        .toc-footer {
                            display: flex;
                            justify-content: space-between;
                            align-items: flex-end;
                            font-size: 14px;
                            color: white;

                            p {
                                margin: 0;
                                line-height: 1.4;
                            }

                            .footer-line1 {
                                font-size: 14px;
                                font-family: 'Helvetica';
                                color: white;
                                font-style: italic;
                                margin-bottom: 20px;
                            }

                            .footer-line2 {
                                font-size: 14px;
                                font-family: 'Helvetica';
                                color: var(--footer-color);
                            }

                            .footer-left {
                                .project-details {
                                    .proj-name {
                                        font-weight: 400;
                                        font-size: 14px;
                                        font-style: italic;
                                    }

                                    .proj-loc {
                                        opacity: 1;
                                    }

                                    .proj-proposal {
                                        margin-top: 5px;
                                        font-weight: 600;
                                    }
                                }
                            }

                            .footer-right {
                                .company-name {
                                    font-weight: 600;
                                    font-size: 14px;
                                }
                            }
                        }
                    }
                }
            }
        }
    }
}
</style>
